import { ReactElement, useState } from 'react'
import {
  Box,
  capitalize,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'

import {
  CancelIcon,
  CloseIcon,
  PendingIcon,
  VerifiedIcon,
} from 'src/v3/core/icons'
import VerificationInfoIcon from '@mui/icons-material/NewReleases'
import { useAppSelector } from 'src/store/hooks'

import { VerificationStatus } from 'src/v3/core/types'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import {
  fetchLinkedServices,
  selectLinkedServices,
  selectLinkedServicesStatus,
} from '../profile.slice'

const verificationStatusColor: Record<VerificationStatus, string> = {
  accepted: 'success',
  pending: 'warning',
  rejected: 'error',
}

const verificationStatusIcon: Record<VerificationStatus, ReactElement> = {
  accepted: <VerifiedIcon color="success" aria-hidden />,
  pending: <PendingIcon color="warning" aria-hidden />,
  rejected: <CancelIcon color="error" aria-hidden />,
}

type VerificationStatusInfoProps = {
  name: string
  verifications: Record<string, VerificationStatus>
  showText?: boolean
}

export let VerificationStatusInfo = ({
  name,
  verifications,
  showText,
}: VerificationStatusInfoProps): ReactElement => {
  let [open, setOpen] = useState(false)

  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let linkedServiceIDs = Object.keys(verifications)
  let status = verifications[linkedServiceIDs[0]] || 'pending'

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  return linkedServiceIDs.length < 2 ? (
    <Tooltip title={`Verification ${capitalize(status)}`}>
      <Box sx={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
        {verificationStatusIcon[status]}
        {showText && (
          <Typography
            color="text.secondary"
            variant="subtitle2"
          >{`Verification ${capitalize(status)}`}</Typography>
        )}
      </Box>
    </Tooltip>
  ) : (
    <>
      <Tooltip title="Verification Status">
        <IconButton size="small" onClick={() => setOpen(true)}>
          <VerificationInfoIcon
            color="primary"
            sx={{
              transform: 'rotate(180deg)',
            }}
          />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <Paper sx={{ minWidth: 300, maxWidth: 600, px: 2, py: 1 }}>
          <Box sx={{ position: 'relative' }}>
            <IconButton
              size="small"
              sx={{ position: 'absolute', right: -8 }}
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogTitle sx={{ textAlign: 'center' }}>
            {name} Verification Status
          </DialogTitle>
          <DialogContent
            sx={{
              px: 2,
              pb: 2,
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            {linkedServiceIDs.map((ls) => (
              <Typography key={ls}>
                {'\u2022'} {linkedServices.find((l) => l.id === ls)?.name || ls}
                :{' '}
                <Typography
                  component="span"
                  sx={{
                    color: verificationStatusColor[verifications[ls]],
                    fontWeight: 'bold',
                  }}
                >
                  {capitalize(verifications[ls])}
                </Typography>
              </Typography>
            ))}
          </DialogContent>
        </Paper>
      </Dialog>
    </>
  )
}
